import {
  CornerDownLeft,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Quote,
  Sparkles,
  SquareCode,
  Stethoscope,
} from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import type { SuggestionProps } from '@tiptap/suggestion';

import { cn } from '@/lib/utils';

import { filterItems, type SlashGroup, type SlashItem } from '../slashMenuItems';

export interface SlashListRef {
  onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

interface ListProps extends SuggestionProps<SlashItem> {
  allItems: SlashItem[];
}

// Each item gets a small lucide glyph in a tinted square — the menu reads as
// editorial type-set rather than a flat list of strings. Falls back to a
// sparkle for any unmatched template id (defensive against future additions).
const ITEM_ICON: Record<string, ReactNode> = {
  'heading-1': <Heading1 className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'heading-2': <Heading2 className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'heading-3': <Heading3 className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'bullet-list': <List className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'ordered-list': <ListOrdered className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'task-list': <ListTodo className="h-3.5 w-3.5" strokeWidth={1.5} />,
  quote: <Quote className="h-3.5 w-3.5" strokeWidth={1.5} />,
  divider: <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'code-block': <SquareCode className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'template-soap': <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.5} />,
  'template-risk': <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.5} />,
};

const GROUP_ORDER: SlashGroup[] = ['Headings', 'Lists', 'Inserts', 'Clinical scaffolds'];

export const SlashList = forwardRef<SlashListRef, ListProps>(function SlashList(
  { allItems, query, command },
  ref,
) {
  const [activeIndex, setActiveIndex] = useState(0);
  const prevQueryRef = useRef(query);
  const items = filterItems(allItems, query);

  // Reset selection when the query changes. React-recommended in-render reset
  // pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // — necessary because the suggestion plugin reuses this component instance
  // across queries; remounting via `key` isn't an option from inside.
  // eslint-disable-next-line react-hooks/refs
  if (prevQueryRef.current !== query) {
    // eslint-disable-next-line react-hooks/refs
    prevQueryRef.current = query;
    setActiveIndex(0);
  }

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowDown') {
          setActiveIndex((i) => (i + 1) % Math.max(items.length, 1));
          return true;
        }
        if (event.key === 'ArrowUp') {
          setActiveIndex((i) => (i - 1 + items.length) % Math.max(items.length, 1));
          return true;
        }
        if (event.key === 'Enter') {
          const item = items[activeIndex];
          if (item !== undefined) {
            command(item);
            return true;
          }
        }
        return false;
      },
    }),
    [items, activeIndex, command],
  );

  if (items.length === 0) {
    return (
      <output
        className="border-accent-ink/10 block w-72 rounded-xl border bg-white px-4 py-3 shadow-lg"
        aria-live="polite"
      >
        <p className="font-display text-accent-ink text-[13px] italic">No matches</p>
        <p className="text-meta-sm mt-0.5 text-neutral-500">
          Try <span className="text-accent-ink/70 font-mono">heading</span>,{' '}
          <span className="text-accent-ink/70 font-mono">soap</span>, or{' '}
          <span className="text-accent-ink/70 font-mono">quote</span>.
        </p>
      </output>
    );
  }

  // Render with group-eyebrow sections when the query is empty (full menu);
  // collapse to a flat list when the user is typing — group headings would
  // just be visual noise on a 2-3 item search result.
  const isSearch = query.trim() !== '';
  const grouped = new Map<SlashGroup, { item: SlashItem; flatIdx: number }[]>();
  items.forEach((item, idx) => {
    const arr = grouped.get(item.group) ?? [];
    arr.push({ item, flatIdx: idx });
    grouped.set(item.group, arr);
  });

  return (
    <div
      role="listbox"
      aria-label="Slash menu commands"
      aria-activedescendant={
        items[activeIndex] !== undefined ? `slash-option-${items[activeIndex].id}` : undefined
      }
      tabIndex={-1}
      className="border-accent-ink/10 flex max-h-80 w-80 flex-col overflow-y-auto rounded-xl border bg-white p-1.5 shadow-lg"
    >
      <div
        aria-hidden
        className="font-display tracking-eyebrow text-accent-ink/55 flex items-center justify-between px-2.5 pt-1.5 pb-1 text-[10px] uppercase italic"
      >
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" strokeWidth={1.5} />
          {isSearch ? `${String(items.length)} matches` : 'Insert block'}
        </span>
        <span className="italic-none font-mono tracking-normal normal-case not-italic">↑ ↓ ↵</span>
      </div>

      {isSearch
        ? items.map((item, idx) => (
            <SlashRow
              key={item.id}
              item={item}
              isActive={idx === activeIndex}
              onHover={() => {
                setActiveIndex(idx);
              }}
              onPick={() => {
                command(item);
              }}
            />
          ))
        : GROUP_ORDER.map((group) => {
            const rows = grouped.get(group);
            if (rows === undefined || rows.length === 0) return null;
            return (
              <div key={group} className="mt-1.5 first:mt-0">
                <div
                  aria-hidden
                  className="font-display tracking-eyebrow text-accent-ink/45 px-2.5 pt-1 pb-0.5 text-[9.5px] uppercase italic"
                >
                  {group}
                </div>
                {rows.map(({ item, flatIdx }) => (
                  <SlashRow
                    key={item.id}
                    item={item}
                    isActive={flatIdx === activeIndex}
                    onHover={() => {
                      setActiveIndex(flatIdx);
                    }}
                    onPick={() => {
                      command(item);
                    }}
                  />
                ))}
              </div>
            );
          })}
    </div>
  );
});

interface RowProps {
  item: SlashItem;
  isActive: boolean;
  onHover: () => void;
  onPick: () => void;
}

function SlashRow({ item, isActive, onHover, onPick }: RowProps) {
  const icon = ITEM_ICON[item.id] ?? <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />;
  return (
    <div
      id={`slash-option-${item.id}`}
      role="option"
      tabIndex={-1}
      aria-selected={isActive}
      onMouseEnter={onHover}
      onClick={onPick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPick();
        }
      }}
      className={cn(
        'group relative flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 transition-colors',
        isActive ? 'bg-primary-50/80' : 'hover:bg-neutral-50',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors',
          isActive
            ? 'border-primary-200/70 text-accent-ink bg-white'
            : 'border-accent-ink/10 bg-neutral-50 text-neutral-600',
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'truncate text-[13px] leading-tight font-medium',
            isActive ? 'text-accent-ink' : 'text-neutral-900',
          )}
        >
          {item.title}
        </span>
        <span className="truncate text-[11.5px] text-neutral-500">{item.description}</span>
      </span>
      {isActive ? (
        <span aria-hidden className="text-accent-ink/60 ml-auto">
          <CornerDownLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
        </span>
      ) : null}
    </div>
  );
}
